import { API_ERROR_CODES } from '@lk/shared';
import { formatTime } from './format';

/**
 * Три різні біди, які треба розрізняти, бо реакція на них РІЗНА:
 *
 *  · NetworkError    — не долетіло. Дані не втрачені, повторимо пізніше.
 *  · ApiHttpError    — сервер відповів помилкою. Повторювати сліпо не можна.
 *  · ApiParseError   — сервер відповів не тим, що обіцяє контракт. Це баг, і його видно.
 *
 * Найгірше, що можна зробити, — звести все до `catch {}`. Тому мовчазних
 * ковтань помилок у застосунку немає: кожна доходить або до UI, або до черги.
 */

export class NetworkError extends Error {
  override readonly name = 'NetworkError';
  override readonly cause: unknown;

  constructor(message = 'Немає звʼязку з сервером', cause?: unknown) {
    super(message);
    this.cause = cause;
  }
}

export class ApiHttpError extends Error {
  override readonly name = 'ApiHttpError';
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Помилки, які не виправить повтор: валідація, конфлікт, заборона. */
  get isPermanent(): boolean {
    if (this.status === 401 || this.status === 408 || this.status === 429) return false;
    return this.status >= 400 && this.status < 500;
  }
}

export class ApiParseError extends Error {
  override readonly name = 'ApiParseError';
  constructor(
    readonly path: string,
    readonly issues: unknown,
  ) {
    super(`Сервер відповів у неочікуваному форматі: ${path}`);
  }
}

/**
 * Чи це «секрет не збігся під час входу» — на відміну від «сесія протухла».
 * Обидві біди приїжджають зі статусом 401, і розрізняє їх лише код.
 */
export function isLoginFailed(error: unknown): boolean {
  return error instanceof ApiHttpError && error.code === API_ERROR_CODES.LOGIN_FAILED;
}

/** Дата з `details`, якщо сервер її поклав і вона справді дата. */
function dateIn(details: unknown, field: string): Date | null {
  const raw = (details as Record<string, unknown> | undefined)?.[field];
  if (typeof raw !== 'string') return null;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at;
}

function detailDate(error: unknown, code: string, field: string): Date | null {
  if (!(error instanceof ApiHttpError)) return null;
  if (error.code !== code) return null;
  return dateIn(error.details, field);
}

/**
 * Момент, коли скінчиться обовʼязкова перерва. `null` — це не про перерву.
 * Час рахує сервер, бо на годинник пристрою в такому питанні покладатися
 * не можна: його переводять.
 */
export function breakResumeAt(error: unknown): Date | null {
  return detailDate(error, API_ERROR_CODES.BREAK_REQUIRED, 'resumeAt');
}

/** Момент, коли розклад відчиниться. `null` — це не про розклад. */
export function scheduleOpensAt(error: unknown): Date | null {
  return detailDate(error, API_ERROR_CODES.SCHEDULE_BLOCKED, 'opensAt');
}

/**
 * Дружній до дитини текст за КОДОМ, а не за винятком.
 *
 * Винесено з `humanMessage` навмисно: рівно ті самі коди тепер приїжджають
 * двома різними шляхами — як HTTP-помилка запиту і як поштучний вирок про
 * одну спробу в тілі 200. Два розʼїхані переліки текстів означали б, що
 * дитина читає різне про одне й те саме.
 *
 * `serverMessage` — запасний текст із сервера. Він теж українською й теж
 * для дитини, але тут ми маємо перевагу: знаємо екран і час.
 */
export function humanMessageForCode(
  code: string,
  serverMessage = '',
  details?: unknown,
): string {
  switch (code) {
    case API_ERROR_CODES.UNAUTHORIZED:
      return 'Потрібно зайти ще раз.';
    case API_ERROR_CODES.LOGIN_FAILED:
      // Текст із сервера навмисно однаковий для «немає такого акаунта» і
      // «невірний секрет». Екрани входу уточнюють його під свій спосіб входу.
      return serverMessage || 'Дані для входу не збіглися.';
    case API_ERROR_CODES.FORBIDDEN:
      return 'Цей розділ доступний лише батькам.';
    case API_ERROR_CODES.SCHEDULE_BLOCKED: {
      // «Повертайся пізніше» на закриту годину серед дня читається як
      // поломка. Точний час читається як правило — і його видно наперед.
      const opensAt = dateIn(details, 'opensAt');
      return opensAt
        ? `Зараз навчання закрите. Відчиниться о ${formatTime(opensAt)}.`
        : 'Зараз не час для навчання. Повертайся у дозволені години.';
    }
    case API_ERROR_CODES.BREAK_REQUIRED:
      return 'Зараз перерва. Підведись, поглянь у вікно — і повертайся.';
    case API_ERROR_CODES.DAILY_LIMIT:
      return 'На сьогодні ліміт вичерпано. Завтра буде нова спроба!';
    case API_ERROR_CODES.RATE_LIMITED:
      // Текст із сервера має перевагу: коли вхід закрито глобальним замком,
      // він каже, скільки саме чекати. «Зачекай трохи» на пів години
      // виглядало б як зламаний застосунок.
      return serverMessage || 'Забагато спроб поспіль. Зачекай трохи.';
    case API_ERROR_CODES.SESSION_CLOSED:
      return 'Ця сесія вже завершена.';
    case API_ERROR_CODES.ITEM_NOT_SERVED:
      // Дитина цього не робила руками: так виглядає збій — питання підмінили,
      // сесію перезібрали. Тому без звинувачень і без слова «заборонено».
      return 'Це питання не з цієї гри — відповідь не зарахована.';
    default:
      return serverMessage || 'Щось пішло не так.';
  }
}

/** Дружній до дитини текст помилки. Ніякого канцеляриту й кодів. */
export function humanMessage(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'Інтернет зник. Не хвилюйся — усі відповіді збережені на пристрої.';
  }
  if (error instanceof ApiHttpError) {
    return humanMessageForCode(error.code, error.message, error.details);
  }
  if (error instanceof ApiParseError) {
    return 'Сервер відповів дивно. Ми це запишемо і розберемось.';
  }
  if (error instanceof Error) return error.message;
  return 'Невідома помилка.';
}
